import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  getCompanyIdForUser,
  findAccountIdByEmail,
  getPrimaryUserIdForAccount,
} from '../services/firestoreService';

/**
 * Resolves the user id that owns this account's roadmap & goals data.
 * Mirrors the logic previously duplicated across the department overview tabs:
 * the signed-in user -> their account -> that account's primary user.
 */
export function useDepartmentOwnerId() {
  const { currentUser } = useAuth();
  const [ownerId, setOwnerId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentUser?.uid) {
      setOwnerId(null);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        let owner = currentUser.uid;
        let cid = await getCompanyIdForUser(currentUser.uid);
        if (!cid && currentUser.email) {
          cid = await findAccountIdByEmail(currentUser.email, 'Clients').catch(() => null);
        }
        if (cid) {
          const primaryUid = await getPrimaryUserIdForAccount(cid);
          if (primaryUid) owner = primaryUid;
        }
        if (!cancelled) setOwnerId(owner);
      } catch (error) {
        console.error('Error resolving department owner id:', error);
        if (!cancelled) setOwnerId(currentUser.uid);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [currentUser?.uid, currentUser?.email]);

  return { ownerId, loading };
}

export default useDepartmentOwnerId;
